import { useState, useEffect, useRef } from "react";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../context/AuthContext";
import { newsletterService } from "../services/api";

const AnimatedStat = ({ value, suffix = "%" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null); const hasAnimated = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const duration = 1600; const startTime = performance.now();
        const animate = (now) => {
          const elapsed = now - startTime; const progress = Math.min(elapsed / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCount(Math.round(eased * value));
          if (progress < 1) requestAnimationFrame(animate);
        };
        requestAnimationFrame(animate);
      }
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value]);
  return <span ref={ref}>{count}{suffix}</span>;
};

const FadeIn = ({ children, delay = 0, direction = "up", style = {} }) => {
  const ref = useRef(null); const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { threshold: 0.1 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  const transforms = { up:"translateY(40px)",left:"translateX(40px)",right:"translateX(-40px)",none:"none" };
  return (
    <div ref={ref} style={{ opacity:visible?1:0, transform:visible?"none":transforms[direction]||"translateY(40px)", transition:`opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`, ...style }}>
      {children}
    </div>
  );
};

const Logo = ({ dark = false, size = 1 }) => (
  <div style={{display:"flex",alignItems:"center",gap:10*size}}>
    <svg width={30*size} height={30*size} viewBox="0 0 32 32" fill="none">
      <path d="M16 2C8.268 2 2 8.268 2 16s6.268 14 14 14 14-6.268 14-14S23.732 2 16 2z" fill="#F5A623"/>
      <path d="M10 18c0-3.314 2.686-6 6-6s6 2.686 6 6" stroke="white" strokeWidth="2.5" strokeLinecap="round"/>
      <path d="M13 15c0-1.657 1.343-3 3-3s3 1.343 3 3" stroke="white" strokeWidth="2" strokeLinecap="round"/>
      <circle cx="16" cy="22" r="1.5" fill="white"/>
    </svg>
    <span style={{fontFamily:"'DM Sans',sans-serif",fontWeight:700,fontSize:18*size,color:dark?"#1a1a2e":"white",letterSpacing:"-0.02em"}}>
      <span style={{color:"#F5A623"}}>Ai</span>SchoolCoach
    </span>
  </div>
);

export const HomeCommunity: React.FC = () => {
  const { t } = useTranslation('home-community');
  const { t: tCommon } = useTranslation('common');
  const { login, isLoading: loginLoading, error: authError, clearError } = useAuth();
  const [loginUser, setLoginUser] = useState("");
  const [loginPass, setLoginPass] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationError, setVerificationError] = useState("");
  const [loginAttempted, setLoginAttempted] = useState(false);
  const [formData, setFormData] = useState({ firstName:"", lastName:"", email:"" });
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleLoginSubmit = async (e) => {
    e.preventDefault(); setLoginAttempted(true); clearError(); setVerificationError("");
    if (verificationCode.toLowerCase() !== "traviata") { setVerificationError(tCommon('login.invalidCredentials')); return; }
    try { await login({ email: loginUser, password: loginPass }); } catch (error) { console.error("Login failed:", error); }
  };
  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await newsletterService.submitSignup({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        leadSource: 'home_community',
        formType: 'guide_signup',
        audienceType: 'community',
      });
      setFormSubmitted(true);
      setTimeout(() => setFormSubmitted(false), 4000);
    } catch (error) {
      console.error('Guide signup error:', error);
    }
  };

  return (
    <div style={{minHeight:"100vh",background:"#FFFFFF",color:"#1a1a2e",fontFamily:"'DM Sans',sans-serif",overflowX:"hidden"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        @keyframes float1{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
        @keyframes float2{0%,100%{transform:translateY(0) rotate(0deg)}50%{transform:translateY(-10px) rotate(3deg)}}
        @keyframes float3{0%,100%{transform:translateY(0)}50%{transform:translateY(-8px)}}
        @keyframes heroSlide{from{opacity:0;transform:translateX(50px)}to{opacity:1;transform:translateX(0)}}
        @keyframes heroFade{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        .dot-f1{animation:float1 4s ease-in-out infinite}.dot-f2{animation:float2 5s ease-in-out 1s infinite}
        .dot-f3{animation:float3 6s ease-in-out .5s infinite}.dot-f4{animation:float1 4.5s ease-in-out 2s infinite}
        .hero-img-anim{animation:heroSlide 1s ease .3s both}.hero-title-anim{animation:heroFade .8s ease both}.hero-btn-anim{animation:heroFade .8s ease .2s both}
        .container{max-width:1140px;margin:0 auto;padding:0 24px}
        .cta-btn{display:inline-flex;align-items:center;gap:12px;background:#F5A623;color:#1a1a2e;font-weight:700;font-size:16px;padding:14px 28px;border-radius:50px;border:none;cursor:pointer;transition:all .3s;font-family:'DM Sans',sans-serif;text-decoration:none}
        .cta-btn:hover{background:#FFB840;transform:translateY(-2px);box-shadow:0 8px 24px rgba(245,166,35,.3)}
        .cta-arrow{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;background:rgba(0,0,0,.1);border-radius:50%}
        .form-input{width:100%;padding:14px 18px;border-radius:10px;border:1.5px solid #E0E0E0;font-size:15px;font-family:'DM Sans',sans-serif;background:white;color:#333;transition:border-color .3s,box-shadow .3s;outline:none}
        .form-input:focus{border-color:#F5A623;box-shadow:0 0 0 3px rgba(245,166,35,.15)}.form-input::placeholder{color:#B0B0B0}
        .submit-btn{background:#3B5BDB;color:white;font-weight:700;font-size:16px;padding:16px 48px;border-radius:50px;border:none;cursor:pointer;transition:all .3s;font-family:'DM Sans',sans-serif}
        .submit-btn:hover{background:#2B4BC9;transform:translateY(-2px);box-shadow:0 8px 24px rgba(59,91,219,.3)}
        .stat-card{text-align:center;padding:20px 16px;flex:1;min-width:180px;max-width:300px}
        .stat-title{font-size:13px;font-weight:700;color:#3B5BDB;margin:12px 0 6px;text-decoration:underline;text-underline-offset:3px}
        .stat-text{font-size:12.5px;color:#555;line-height:1.6;font-weight:400}
        .feature-card{display:flex;align-items:flex-start;gap:12px;padding:16px;background:#FAFAFA;border-radius:14px;border:1px solid #F0F0F0;transition:all .3s;flex:1;min-width:240px}
        .feature-card:hover{background:#FFF8EE;border-color:rgba(245,166,35,.25);transform:translateY(-4px);box-shadow:0 8px 24px rgba(0,0,0,.06)}
        .feat-title{font-size:12px;font-weight:700;color:#F5A623;margin-bottom:3px;text-decoration:underline;text-underline-offset:2px}
        .feat-text{font-size:12px;color:#666;line-height:1.55;font-weight:400}
        .toggle-track{width:52px;height:28px;border-radius:14px;background:#F5A623;position:relative;cursor:pointer;flex-shrink:0}
        .toggle-thumb{width:22px;height:22px;border-radius:50%;background:white;position:absolute;top:3px;right:3px;box-shadow:0 2px 4px rgba(0,0,0,.2)}
        .top-login{position:absolute;top:16px;right:24px;display:flex;flex-direction:column;align-items:stretch;gap:6px;z-index:10;width:160px}
        .top-login input{padding:8px 12px;border-radius:8px;border:1.5px solid #E0E0E0;font-size:13px;font-family:'DM Sans',sans-serif;outline:none;transition:border-color .3s;width:100%}
        .top-login input:focus{border-color:#F5A623;box-shadow:0 0 0 2px rgba(245,166,35,.15)}
        .top-login button{padding:8px 16px;border-radius:8px;border:none;background:#3B5BDB;color:white;font-weight:600;font-size:13px;cursor:pointer;width:100%}
        .top-login button:hover{background:#2B4BC9}.top-login .login-err{font-size:12px;color:#E53935;font-weight:600;text-align:center}
        .audience-badge{display:inline-block;background:rgba(123,107,196,.12);color:#7B6BC4;border:1px solid rgba(123,107,196,.28);font-weight:700;font-size:11px;padding:4px 14px;border-radius:50px;margin-bottom:16px;letter-spacing:.06em;text-transform:uppercase}
        @media(max-width:768px){
          .hero-flex{flex-direction:column!important}.stats-flex{flex-direction:column!important;align-items:center!important}
          .features-flex{grid-template-columns:1fr!important}.building-flex{flex-direction:column!important}
          .solution-flex{flex-direction:column!important}.footer-flex{flex-direction:column!important;gap:32px!important}
        }
      `}</style>

      {/* HERO */}
      <section style={{position:"relative",padding:"10px 0 20px",minHeight:"45vh",display:"flex",flexDirection:"column"}}>
        <form onSubmit={handleLoginSubmit} className="top-login">
          <input type="text" placeholder={tCommon('login.emailPlaceholder')} value={loginUser} onChange={(e)=>{setLoginUser(e.target.value);clearError();}}/>
          <input type="password" placeholder={tCommon('login.passwordPlaceholder')} value={loginPass} onChange={(e)=>{setLoginPass(e.target.value);clearError();}}/>
          <input type="text" placeholder={tCommon('login.verificationCodePlaceholder')} value={verificationCode} onChange={(e)=>{setVerificationCode(e.target.value);setVerificationError("");}} autoComplete="off"/>
          <button type="submit" disabled={loginLoading||!loginUser||!loginPass||!verificationCode}>{loginLoading?tCommon('login.loginLoading'):tCommon('login.loginButton')}</button>
          {verificationError&&<span className="login-err">{verificationError}</span>}
          {loginAttempted&&authError&&<span className="login-err">{authError}</span>}
        </form>
        <img src="/img/1st Block/GreenDot_1st Block.png" alt="" className="dot-f1" style={{position:"absolute",top:"14%",left:"12%",width:30,height:30}}/>
        <img src="/img/1st Block/YellowDot_1st Block.png" alt="" className="dot-f2" style={{position:"absolute",top:"6%",right:"6%",width:20,height:20}}/>
        <img src="/img/1st Block/PurpleDot_1st Block.png" alt="" className="dot-f3" style={{position:"absolute",bottom:"18%",right:"45%",width:38,height:38}}/>
        <img src="/img/1st Block/OrangeDot_1st Block.png" alt="" className="dot-f4" style={{position:"absolute",bottom:"6%",left:"3%",width:44,height:44}}/>
        <div className="container hero-flex" style={{display:"flex",alignItems:"center",gap:48,flex:1,paddingLeft:30}}>
          <div style={{flex:1,minWidth:280}}>
            <div style={{paddingBottom:24}}><Logo dark/></div>
            <div className="audience-badge">{t('hero.badge')}</div>
            <h1 className="hero-title-anim" style={{fontSize:"clamp(36px,5vw,58px)",fontWeight:800,lineHeight:1.08,letterSpacing:"-0.03em",marginBottom:20,color:"#1a1a2e"}}>
              {t('hero.titleLine1')}<br/>{t('hero.titleLine2')}<br/><span style={{background:"linear-gradient(135deg,#F5A623,#FF8C42)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{t('hero.titleHighlight')}</span>
            </h1>
            <p style={{fontSize:16,color:"#555",lineHeight:1.7,marginBottom:28,maxWidth:420}}>
              {t('hero.subtitle')}
            </p>
            <div className="hero-btn-anim">
              <button className="cta-btn" onClick={()=>document.getElementById("guide-section")?.scrollIntoView({behavior:"smooth"})}>
                {t('hero.cta')}<span className="cta-arrow">→</span>
              </button>
            </div>
          </div>
          <div className="hero-img-anim" style={{flex:1,display:"flex",justifyContent:"center",position:"relative"}}>
            <img src="/img/1st Block/Picture Banner Home Page.png" alt={t('hero.imageAlt')} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
          </div>
        </div>
      </section>

      {/* THE COMMUNITY GAP */}
      <section style={{padding:"40px 0 20px",position:"relative"}}>
        <img src="/img/2nd Block/Purple_2ndBlock.png" alt="" className="dot-f2" style={{position:"absolute",top:30,left:"7%",width:26,height:26}}/>
        <img src="/img/2nd Block/Green_2ndBlock.png" alt="" className="dot-f4" style={{position:"absolute",bottom:40,right:"5%",width:18,height:18}}/>
        <div className="container" style={{textAlign:"center"}}>
          <FadeIn>
            <h2 style={{fontSize:"clamp(28px,4vw,42px)",fontWeight:800,letterSpacing:"-0.02em",marginBottom:8,color:"#1a1a2e"}}>
              {t('gap.heading')}
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <p style={{fontSize:18,color:"#F5A623",fontWeight:400,maxWidth:520,margin:"0 auto 48px",lineHeight:1.7}}>
              <AnimatedStat value={57}/>{t('gap.statTextSuffix')}
            </p>
          </FadeIn>
          <div className="stats-flex" style={{display:"flex",justifyContent:"center",gap:24,flexWrap:"wrap"}}>
            {[
              {icon:"/img/2nd Block/PersonalFinance_2ndBlock.png",title:t('gap.stats.justiceIssue.title'),text:t('gap.stats.justiceIssue.text')},
              {icon:"/img/2nd Block/EducationalGap_2ndBlock.png",title:t('gap.stats.wealthGap.title'),text:t('gap.stats.wealthGap.text')},
              {icon:"/img/2nd Block/RealLifeReadiness_2nsBlock.png",title:t('gap.stats.communityRoi.title'),text:t('gap.stats.communityRoi.text')},
            ].map((c,i)=>(
              <FadeIn key={i} delay={0.15+i*0.12}>
                <div className="stat-card">
                  <img src={c.icon} alt={c.title} style={{width:80,height:80,objectFit:"contain",margin:"0 auto"}}/>
                  <div className="stat-title">{c.title}</div>
                  <div className="stat-text">{c.text}</div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section style={{padding:"48px 0 56px"}}>
        <div className="container">
          <FadeIn>
            <div style={{maxWidth:740,margin:"0 auto",textAlign:"center",padding:"40px 24px"}}>
              <img src="/img/3rd Block/Yellow_3rdBlock.png" alt="" className="dot-f1" style={{width:14,height:14,margin:"0 auto 20px"}}/>
              <p style={{fontSize:"clamp(16px,2.2vw,20px)",lineHeight:1.7,fontWeight:700,color:"#1a1a2e"}}>
                <span style={{color:"#3B5BDB",fontWeight:800}}>{t('mission.brandName')}</span>{" "}
                {t('mission.text')}
              </p>
              <img src="/img/3rd Block/Orange_3rdBlock.png" alt="" className="dot-f3" style={{width:16,height:16,margin:"24px auto 0"}}/>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* BUILDING LIFE-READY */}
      <section style={{padding:"56px 0 72px",position:"relative",background:"linear-gradient(180deg,#EEF2FF 0%,#FFFFFF 100%)"}}>
        <img src="/img/3rd Block/Yellow_3rdBlock.png" alt="" className="dot-f1" style={{position:"absolute",top:16,left:"14%",width:12,height:12}}/>
        <div className="container">
          <div className="building-flex" style={{display:"flex",alignItems:"center",gap:48}}>
            <FadeIn direction="right" style={{flex:1}}>
              <div style={{borderRadius:20,overflow:"hidden",width:"100%",maxWidth:400,aspectRatio:"4/3"}}>
                <img src="/img/3rd Block/Image_3rdBlock.png" alt={t('building.imageAlt')} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              </div>
            </FadeIn>
            <FadeIn delay={0.15} style={{flex:1,minWidth:280}}>
              <div>
                <div style={{display:"flex",alignItems:"center",gap:16,marginBottom:14}}>
                  <h2 style={{fontSize:"clamp(26px,3.5vw,38px)",fontWeight:800,letterSpacing:"-0.02em",color:"#1a1a2e"}}>
                    {t('building.heading')}
                  </h2>
                  <div className="toggle-track"><div className="toggle-thumb"/></div>
                </div>
                <p style={{fontSize:14,color:"#F5A623",fontWeight:400,lineHeight:1.7,maxWidth:420}}>
                  <AnimatedStat value={44}/>{t('building.statTextSuffix')}
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* PRACTICAL SOLUTION */}
      <section style={{padding:"72px 0 80px",position:"relative"}}>
        <img src="/img/1st Block/OrangeDot_1st Block.png" alt="" className="dot-f3" style={{position:"absolute",top:10,left:"5%",width:16,height:16}}/>
        <img src="/img/1st Block/PurpleDot_1st Block.png" alt="" className="dot-f1" style={{position:"absolute",bottom:30,right:"8%",width:20,height:20}}/>
        <div className="container">
          <div className="solution-flex" style={{display:"flex",alignItems:"flex-start",gap:48,flexWrap:"wrap",marginBottom:48}}>
            <FadeIn style={{flex:"1 1 300px"}}>
              <h2 style={{fontSize:"clamp(28px,4vw,42px)",fontWeight:800,letterSpacing:"-0.02em",lineHeight:1.15,color:"#1a1a2e"}}>
                {t('solution.heading')}
              </h2>
            </FadeIn>
            <FadeIn delay={0.1} direction="left" style={{flex:"1 1 300px"}}>
              <div style={{borderRadius:20,overflow:"hidden",maxWidth:420}}>
                <img src="/img/4th Block/Image_4th block.png" alt={t('solution.imageAlt')} style={{width:"100%",height:"100%",objectFit:"cover"}}/>
              </div>
            </FadeIn>
          </div>
          <div className="features-flex" style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
            {[
              {icon:"/img/4th Block/HandsOn_ICON ONLY.png",title:t('solution.features.realLifeLearning.title'),text:t('solution.features.realLifeLearning.text')},
              {icon:"/img/4th Block/MeasurableResults_ICON ONLY.png",title:t('solution.features.communityResources.title'),text:t('solution.features.communityResources.text')},
              {icon:"/img/4th Block/EasyImplementation_ICON ONLY.png",title:t('solution.features.scalable.title'),text:t('solution.features.scalable.text')},
              {icon:"/img/4th Block/ AddesValue_ICON ONLY.png",title:t('solution.features.intergenerational.title'),text:t('solution.features.intergenerational.text')},
            ].map((f,i)=>(
              <FadeIn key={i} delay={0.1+i*0.08}>
                <div className="feature-card">
                  <img src={f.icon} alt={f.title} style={{width:40,height:40,objectFit:"contain",flexShrink:0}}/>
                  <div><div className="feat-title">{f.title}</div><div className="feat-text">{f.text}</div></div>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* GUIDE SIGNUP */}
      <section id="guide-section" style={{padding:"80px 0",position:"relative",background:"#F8F9FC"}}>
        <img src="/img/5th Block/Yellow_5thBlock.png" alt="" className="dot-f2" style={{position:"absolute",top:24,left:"10%",width:14,height:14}}/>
        <img src="/img/5th Block/Orange_5thBlock.png" alt="" className="dot-f4" style={{position:"absolute",bottom:40,right:"7%",width:18,height:18}}/>
        <img src="/img/5th Block/Green_5thBlock.png" alt="" className="dot-f1" style={{position:"absolute",top:"50%",right:"4%",width:12,height:12}}/>
        <div className="container">
          <FadeIn>
            <div style={{maxWidth:500,margin:"0 auto",background:"white",borderRadius:20,padding:"44px 32px",boxShadow:"0 12px 48px rgba(0,0,0,.08)",border:"1px solid #F0F0F0"}}>
              <div style={{textAlign:"center",marginBottom:8}}><span style={{fontSize:32}}>🏘️</span></div>
              <h2 style={{fontSize:19,fontWeight:700,color:"#1a1a2e",textAlign:"center",marginBottom:4,letterSpacing:"-0.01em",lineHeight:1.4}}>
                {t('guideSignup.heading')}<br/><span style={{color:"#F5A623"}}>{t('guideSignup.headingHighlight')}</span>
              </h2>
              <p style={{fontSize:13,color:"#888",textAlign:"center",marginBottom:20,lineHeight:1.6}}>
                {t('guideSignup.subtitle')}
              </p>
              {formSubmitted ? (
                <div style={{textAlign:"center",padding:"40px 20px"}}>
                  <div style={{fontSize:48,marginBottom:16}}>✅</div>
                  <p style={{fontSize:18,fontWeight:600}}>{tCommon('guideForm.successHeading')}</p>
                  <p style={{fontSize:14,color:"#888",marginTop:8}}>{tCommon('guideForm.successSubtext')}</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} style={{display:"flex",flexDirection:"column",gap:12}}>
                  <input className="form-input" name="firstName" placeholder={tCommon('guideForm.firstNamePlaceholder')} value={formData.firstName} onChange={handleChange} required/>
                  <input className="form-input" name="lastName" placeholder={tCommon('guideForm.lastNamePlaceholder')} value={formData.lastName} onChange={handleChange} required/>
                  <input className="form-input" name="email" type="email" placeholder={tCommon('guideForm.emailPlaceholder')} value={formData.email} onChange={handleChange} required/>
                  <div style={{textAlign:"center",marginTop:10}}>
                    <button type="submit" className="submit-btn">{tCommon('guideForm.submitButton')}</button>
                  </div>
                  <p style={{fontSize:11,color:"#BBB",textAlign:"center",marginTop:4}}>{tCommon('guideForm.noSpam')}</p>
                </form>
              )}
            </div>
          </FadeIn>
        </div>
      </section>

      {/* FOOTER */}
      <footer style={{padding:"56px 0 40px",background:"#0D0D1A",color:"white",position:"relative"}}>
        <img src="/img/6th Block/dots_6thBlock.png" alt="" className="dot-f3" style={{position:"absolute",top:-6,right:"12%",width:12,height:12}}/>
        <img src="/img/6th Block/dots_6thBlock.png" alt="" className="dot-f2" style={{position:"absolute",bottom:16,right:"5%",width:14,height:14}}/>
        <div className="container">
          <div className="footer-flex" style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:48}}>
            <div>
              <Logo/>
              <h3 style={{fontSize:"clamp(28px,4vw,42px)",fontWeight:800,marginTop:10,lineHeight:1.1}}>
                <span style={{background:"linear-gradient(135deg,#F5A623,#FF8C42)",WebkitBackgroundClip:"text",WebkitTextFillColor:"transparent"}}>{tCommon('footer.innovativeAi')}</span>
              </h3>
              <p style={{color:"rgba(255,255,255,.5)",fontSize:13,marginTop:6}}>{tCommon('footer.tagline')}</p>
            </div>
            <div>
              <div style={{display:"inline-block",background:"#F5A623",color:"#0D0D1A",fontWeight:700,fontSize:13,padding:"8px 20px",borderRadius:50,marginBottom:16}}>
                {tCommon('footer.keepInTouch')}
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
